import { useState, useCallback } from 'react'

import type { AxiosError } from 'axios'

type OptionsProps<TData> = {
  onSuccess?: (data: TData) => void
  onError?: (error: Error) => void
}

export const useRequest = <TData, TParams = void>(
  requestFunc: (params: TParams) => Promise<{ data: TData }>,
  options?: OptionsProps<TData>,
) => {
  const [data, setData] = useState<TData | null>(null)
  const [error, setError] = useState<Error | AxiosError | null>(null)
  const [isLoading, setLoading] = useState<boolean>(false)

  const doRequest = useCallback(
    (params: TParams) => {
      setLoading(true)
      setError(null)

      requestFunc(params)
        .then(({ data }: { data: TData }) => {
          setData(data)
          options?.onSuccess?.(data)
        })
        .catch((error: Error | AxiosError) => {
          setData(null)
          setError(error)
          options?.onError?.(error)
        })
        .finally(() => setLoading(false))
    },
    [requestFunc, options],
  )

  return {
    doRequest,
    isLoading,
    error,
    data,
    setData,
  }
}
